import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useCallback, useContext, useMemo, useState } from "react";
import AuditPDFTemplate from "../../../components/PDF/Reporting/audit/AuditPDFTemplate";
import ReportingFormDrawerV2 from "../../../components/Reporting/ReportingFormDrawerV2";
import { ReportStreamColumn } from "../../../components/Reporting/stream/ReportStreamList";
import ReportStreamPage from "../../../components/Reporting/stream/ReportStreamPage";
import {
	ReportStreamItemsSummary,
	ReportStreamMeta,
	ReportStreamNumberCell,
	ReportStreamRowMenu,
	ReportStreamStatusChip,
	ReportStreamTitle,
} from "../../../components/Reporting/stream/ReportStreamParts";
import { HomeContext } from "../../../containers/Home";
import {
	ReportStreamConfig,
	useReportStream,
} from "../../../hooks/useReportStream";
import {
	useReportStreamNames,
	useTenantName,
} from "../../../hooks/useReportStreamLookups";
import { ReportItem } from "../../../hooks/useReportStreamFilter";
import { fetchReportingHubReports } from "../../../services/reporting-hub/fetchReports";
import { useTenantStore } from "../../../stores/tenantStore";
import { formatDateDdMmmYyyy } from "../../../utils/date_formatter";
import {
	collectAuditExportEmails,
	collectAuditorEmails,
	getAuditee,
	getAuditExportData,
	getAuditMeta,
	getAuditSearchTexts,
	getLeadAuditor,
	StreamAudit,
} from "../../../utils/reporting/auditStream";
import { getAuditTypeLabel } from "../../../utils/reporting/auditTypes";
import { REPORT_ITEM_KINDS } from "../../../utils/reporting/reportItemKinds";
import {
	getDateValue,
	getItemsSummary,
	getReportNumberValue,
} from "../../../utils/reporting/reportStream";
import {
	DEFAULT_REPORT_STATUSES,
	ReportStatusKey,
} from "../../../utils/reporting/reportStatusOptions";
import { fetchExportNames } from "../../../utils/reporting/reportStreamExport";
import { handleExportReport } from "../../../utils/reports";

type AuditRow = ReportItem &
	StreamAudit & {
		date_and_time_of_audit?: string;
		is_rejected?: boolean;
	};

// Audits never reach Approved - Open, so they have four figures.
const AUDIT_STATUSES: ReportStatusKey[] = [
	"in_review",
	"in_progress",
	"final_review",
	"closed",
];

const AUDIT_STREAM: ReportStreamConfig<AuditRow> = {
	statuses: AUDIT_STATUSES,
	defaultStatuses: AUDIT_STATUSES.filter((key) =>
		DEFAULT_REPORT_STATUSES.includes(key)
	),
	hasInternalExternal: true,
	filters: [
		{
			key: "type",
			label: "Audit type",
			getValue: (audit) => audit.audit_type,
			getOptionLabel: getAuditTypeLabel,
		},
	],
	sortValues: {
		number: (audit) => getReportNumberValue(audit.report_number),
		date: (audit) => getDateValue(audit.date_and_time_of_audit),
	},
};

const getAuditPath = (audit: AuditRow) =>
	`/reporting/audit-stream/${audit._id}`;

// The audit's PDF, with the names of everyone it names, looked up for this
// audit alone. Without them it still exports, with their emails, as the audit
// page does when its lookup fails.
async function exportAudit(audit: AuditRow) {
	const emailToName = await fetchExportNames(collectAuditExportEmails(audit));
	await handleExportReport(
		getAuditExportData(audit, emailToName),
		"audit",
		AuditPDFTemplate
	);
}

// An audit's title, the line with its type, department and customer, and its
// auditee.
function AuditTitleCell({
	audit,
	auditee,
}: Readonly<{ audit: AuditRow; auditee: string }>) {
	const { type, details } = getAuditMeta(audit);
	return (
		<>
			<ReportStreamTitle title={audit.title} />
			<ReportStreamMeta tag={type} parts={details} />
			{auditee && (
				<span className="report-stream__meta">{`Auditee: ${auditee}`}</span>
			)}
		</>
	);
}

function AuditStream() {
	const { currentTenantKey }: any = useContext(HomeContext);
	const isMasterTenant = useTenantStore((state) => state.isMasterTenant);
	const getTenantName = useTenantName();
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);

	// The Reporting Hub fills and reads the same entry, and a saved report
	// is written into it (ReportingFormDrawerV2), so the rows stay as loaded.
	const { data, isPending, isError, isFetching, refetch } = useQuery({
		queryKey: ["reporting-hub-data", "audit"],
		queryFn: () =>
			fetchReportingHubReports("audit", currentTenantKey, isMasterTenant),
		enabled: !!currentTenantKey,
		staleTime: 5 * 60 * 1000,
	});
	const audits: AuditRow[] | undefined = Array.isArray(data) ? data : undefined;

	// Only the listed lead auditors are looked up by name.
	const auditorEmails = useMemo(
		() => collectAuditorEmails(audits ?? []),
		[audits]
	);
	const auditorNames = useReportStreamNames(
		["audit-stream-auditors"],
		auditorEmails
	);

	const getSearchTexts = useCallback(
		(audit: AuditRow) =>
			getAuditSearchTexts(audit, auditorNames, getTenantName),
		[auditorNames, getTenantName]
	);

	const view = useReportStream(
		audits,
		AUDIT_STREAM,
		getSearchTexts,
		currentTenantKey,
		isMasterTenant
	);

	const today = dayjs();
	const columns: ReportStreamColumn<AuditRow>[] = [
		{
			key: "number",
			header: "Report no.",
			width: "140px",
			narrow: "number",
			sortKey: "number",
			render: (audit) => (
				<ReportStreamNumberCell
					to={getAuditPath(audit)}
					reportNumber={audit.report_number}
					operator={view.hasSeveralOperators ? audit.operator : undefined}
				/>
			),
		},
		{
			key: "audit",
			header: "Audit",
			width: "minmax(180px, 1fr)",
			narrow: "main",
			render: (audit) => (
				<AuditTitleCell
					audit={audit}
					auditee={getAuditee(audit, getTenantName)}
				/>
			),
		},
		{
			key: "date",
			header: "Audit date",
			width: "104px",
			narrow: "detail",
			sortKey: "date",
			render: (audit) => formatDateDdMmmYyyy(audit.date_and_time_of_audit),
		},
		{
			key: "auditor",
			header: "Lead auditor",
			width: "140px",
			narrow: "detail",
			render: (audit) => (
				<span className="report-stream__ellipsis">
					{getLeadAuditor(audit, auditorNames)}
				</span>
			),
		},
		{
			key: "findings",
			header: "Findings",
			width: "148px",
			narrow: "full",
			render: (audit) => (
				<ReportStreamItemsSummary
					kind={REPORT_ITEM_KINDS.findings}
					summary={getItemsSummary(audit.findings, audit, today)}
				/>
			),
		},
		{
			key: "status",
			header: "Status",
			width: "132px",
			narrow: "status",
			render: (audit) => (
				<ReportStreamStatusChip
					status={audit.status}
					openStatus={audit.open_status}
				/>
			),
		},
	];

	return (
		<div className="generic-container overflow-auto">
			<ReportStreamPage
				title="Audits"
				subtitle={
					isMasterTenant
						? "Audits from every tenant."
						: "Your tenant's audits, and external audits from other tenants."
				}
				noun="audit"
				plural="audits"
				newLabel="New audit"
				onNew={() => setIsDrawerOpen(true)}
				emptyText={
					isMasterTenant
						? "Audits from every tenant will show here."
						: "Audits your tenant adds, and external audits from other tenants, will show here."
				}
				searchPlaceholder="Search number, title, department, customer, auditor"
				view={view}
				isLoading={isPending}
				loadFailed={isError && !audits}
				isRetrying={isFetching}
				onRetry={() => void refetch()}
				columns={columns}
				getRowKey={(audit) => audit._id}
				renderRowMenu={(audit) => (
					<ReportStreamRowMenu
						reportNumber={audit.report_number}
						path={getAuditPath(audit)}
						tenantKey={currentTenantKey}
						onExport={() => exportAudit(audit)}
					/>
				)}
			/>
			<ReportingFormDrawerV2
				isOpen={isDrawerOpen}
				reportType="audit"
				onClose={() => setIsDrawerOpen(false)}
			/>
		</div>
	);
}

export default AuditStream;
